import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import ContactDynamicMultiSelectInput from 'components/party/ContactDynamicMultiSelectInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import { FuzzySearchTableView, useFuzzySearch } from 'hooks/useFuzzySearch';
import { useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { ClassifierData } from 'types/classifiers';
import { contactKind, ListedIn, Setting } from 'types/back-office-settings';
import { ContactForm, PartyType, RelationshipTypeCode } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useRequiredFields } from 'utils/useRequiredFields';
import { useSettings } from 'utils/useSettings';
import InfoTooltip from '../../InfoTooltip.tsx';
import AddressInput from '../../forms/inputs/rhf-inputs/AddressInput.tsx';
import DynamicMultiSelectInput from '../../forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import MultiSelectInput from '../../forms/inputs/rhf-inputs/MultiSelectInput.tsx';
import PhoneNumberInput from '../../forms/inputs/rhf-inputs/PhoneNumberInput';

interface Props {
    classifiers: Record<string, ClassifierData>;
    control: Control<ContactForm>;
    setValue: UseFormSetValue<ContactForm>;
    prefillInformalName: (value: string | undefined | null, composedWith: 'firstName' | 'lastName') => void;
    groupSortOrder?: string[];
    requireEmail?: boolean;
    relationshipType?: string;
    fuzzySearch: ReturnType<typeof useFuzzySearch>;
    isCorrespondence?: boolean;
}

export const AddPartyPersonForm = ({
    classifiers,
    control,
    setValue,
    prefillInformalName,
    groupSortOrder,
    requireEmail,
    relationshipType,
    fuzzySearch,
    isCorrespondence = false,
}: Props) => {
    const { fuzzySearchStates, updateSearchState, searchHandlers } = fuzzySearch;

    const { isSettingEnabled } = useSettings();
    const isCorrespondenceAddressEnabled = isSettingEnabled(Setting.CORRESPONDENCE_ADDRESS);
    // The Client form's Required fields card holds for the fields this window shows, beside its own correspondence rules.
    const { isRequired } = useRequiredFields();
    const kind = contactKind(PartyType.PERSON, relationshipType as RelationshipTypeCode | undefined);
    const correspondenceLead = isCorrespondence && relationshipType === RelationshipTypeCode.LEAD;
    const lastNameFirst = useIsFeatureEnabled(FeatureFlag.LAST_NAME_FIRST);

    const watchedHomeAddress = useWatch({ control, name: 'homeAddress' });
    const watchedCorrespondenceAddress = useWatch({ control, name: 'correspondenceAddress' });

    const needsConditionalAddressValidation = isCorrespondence && relationshipType !== RelationshipTypeCode.CLIENT;
    const isHomeAddressFilled = !!watchedHomeAddress?.address1;
    const isCorrespondenceAddressFilled = !!watchedCorrespondenceAddress?.address1;

    const languageOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['LANGUAGE']).filter(
                (option) => !option.parameters || option.parameters['hideForTimecard'] !== true
            ) as {
                label: string;
                value: string;
            }[]) ?? []
        );
    }, [classifiers]);

    const businessAreaOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']) as {
                label: string;
                value: string | number;
            }[]) ?? []
        );
    }, [classifiers]);

    const marketingLanguageOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['LANGUAGE']).filter(
                (option) => !option.parameters || option.parameters['hideForMarketing'] !== true
            ) as {
                label: string;
                value: string;
            }[]) ?? []
        );
    }, [classifiers]);

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                    {(() => {
                        const firstNameItem = (
                            <div className="flex-1">
                                <TextInput
                                    name="firstName"
                                    control={control}
                                    label="first-name"
                                    required
                                    placeholder="forms.enter-first-name"
                                    maxLength={100}
                                    onBlur={(val) => prefillInformalName(val, 'lastName')}
                                />
                            </div>
                        );
                        const lastNameItem = (
                            <div className="flex-1">
                                <TextInput
                                    name="lastName"
                                    control={control}
                                    label="last-name"
                                    required
                                    placeholder="forms.enter-last-name"
                                    maxLength={100}
                                    onBlur={(val) => prefillInformalName(val, 'firstName')}
                                />
                            </div>
                        );
                        return lastNameFirst ? (
                            <>
                                {lastNameItem}
                                {firstNameItem}
                            </>
                        ) : (
                            <>
                                {firstNameItem}
                                {lastNameItem}
                            </>
                        );
                    })()}
                </div>
                {/* The person's unofficial name field is hidden; prefillInformalName fills party.name
                    with "First Last" on first/last blur. */}
                <FuzzySearchTableView
                    fuzzySearchResults={fuzzySearchStates.NAME.results}
                    partyType={PartyType.PERSON}
                    isAccordionExpanded={fuzzySearchStates.NAME.isExpanded}
                    setIsAccordionExpanded={(expanded) => updateSearchState('NAME', { isExpanded: expanded })}
                    classifiers={classifiers}
                />
                {relationshipType &&
                (relationshipType === RelationshipTypeCode.CLIENT ||
                    relationshipType === RelationshipTypeCode.LEAD ||
                    (isCorrespondence && relationshipType === RelationshipTypeCode.NON_CLIENT)) ? (
                    <>
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="contacts.about.relationship-partner"
                            name="partners"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_PARTNER } },
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            placeholder="contacts.about.relationship-partner"
                            required={
                                relationshipType === RelationshipTypeCode.CLIENT ||
                                (isCorrespondence && relationshipType === RelationshipTypeCode.LEAD)
                            }
                        />
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="contacts.about.relationship-manager"
                            name="managers"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                                additionalParams: { extraParams: { listedIn: ListedIn.CONTACT_RELATIONSHIP_MANAGER } },
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            placeholder="contacts.about.relationship-manager"
                            required={isRequired('CLIENT', 'RELATIONSHIP_MANAGER', kind) || correspondenceLead}
                        />
                    </>
                ) : null}
                {relationshipType && relationshipType === RelationshipTypeCode.CLIENT ? (
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="contacts.billing.invoice-lang"
                        name="preferredLanguage"
                        options={languageOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="contacts.billing.invoice-lang"
                        required={relationshipType === RelationshipTypeCode.CLIENT}
                    />
                ) : null}
                {relationshipType === RelationshipTypeCode.CLIENT ||
                (isCorrespondence &&
                    (relationshipType === RelationshipTypeCode.LEAD ||
                        relationshipType === RelationshipTypeCode.NON_CLIENT)) ? (
                    <MultiSelectInput
                        control={control}
                        name="businessAreas"
                        label="business-areas"
                        placeholder="business-areas"
                        options={businessAreaOptions}
                        setValue={setValue}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        sortDirection="asc"
                        required={isRequired('CLIENT', 'BUSINESS_AREAS', kind) || correspondenceLead}
                    />
                ) : null}
                <AddressInput
                    label="address"
                    name="homeAddress"
                    control={control}
                    classifiers={classifiers}
                    clearable
                    required={
                        relationshipType === RelationshipTypeCode.CLIENT ||
                        (needsConditionalAddressValidation && !isCorrespondenceAddressFilled)
                    }
                />
                {(isCorrespondenceAddressEnabled || isCorrespondence) && (
                    <AddressInput
                        label="contacts.correspondence-address"
                        name="correspondenceAddress"
                        control={control}
                        classifiers={classifiers}
                        clearable
                        required={needsConditionalAddressValidation && !isHomeAddressFilled}
                    />
                )}
                <ContactDynamicMultiSelectInput
                    control={control}
                    name="businessContacts"
                    label="contacts.business-contact-of"
                    placeholder="contacts.business-contact-of"
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    setValue={setValue}
                />
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contacts.marketing.preferred-mailing-language"
                    name="preferredMailingLanguageTypeCode"
                    options={marketingLanguageOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    placeholder="contacts.marketing.preferred-mailing-language"
                    required={isRequired('CLIENT', 'MAILING_LANGUAGE', kind) || isCorrespondence}
                />
                <MultiSelectInput
                    control={control}
                    name="marketingAreaTypes"
                    label="contacts.marketing.preferred-marketing-areas"
                    placeholder="contacts.marketing.preferred-marketing-areas"
                    tooltip={<InfoTooltip text="contacts.marketing.tooltips.preferred-marketing-areas" />}
                    options={getValidOptionsFromClassifier<string, string>(classifiers['MARKETING_AREA_TYPE']) ?? []}
                    setValue={setValue}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                    hasSelectAllOption
                />
                {isSettingEnabled(Setting.PERSON_TITLES_FREE_TEXT) ? (
                    <TextInput control={control} name="personManualTitle" label="title" placeholder="title" />
                ) : (
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="title"
                        name="personTitle"
                        options={
                            (getValidOptionsFromClassifier(classifiers['PERSON_TITLE']) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="title"
                    />
                )}
                <TextInput
                    name="personalCode"
                    control={control}
                    label="contacts.person-id"
                    placeholder="contacts.person-id"
                    required={isRequired('CLIENT', 'PERSONAL_ID_CODE', kind)}
                    maxLength={20}
                    onChange={searchHandlers?.personalCode?.handler}
                />
                <FuzzySearchTableView
                    fuzzySearchResults={fuzzySearchStates.PERSONAL_ID.results}
                    partyType={PartyType.PERSON}
                    isAccordionExpanded={fuzzySearchStates.PERSONAL_ID.isExpanded}
                    setIsAccordionExpanded={(expanded) => updateSearchState('PERSONAL_ID', { isExpanded: expanded })}
                    classifiers={classifiers}
                />
                <TextInput
                    name="position"
                    control={control}
                    label="contacts.position"
                    placeholder="contacts.position"
                />
            </div>
            <PhoneNumberInput name="mainPhone" control={control} label="phone" placeholder="phone" />
            <TextInput
                name="mainEmail"
                control={control}
                label="email"
                placeholder="email"
                maxLength={255}
                validate={(val) => isValidEmail(val, t('validation.email-must-be-valid'))}
                required={requireEmail}
            />
            <PhoneNumberInput name="mainMobile" control={control} label="mobile-phone" placeholder="mobile-phone" />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="contacts.employed-by"
                name="employedBy"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.COMPANY}`,
                    searchField: ['name', 'partyNumber'],
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                placeholder="contacts.employed-by"
                groupSortOrder={groupSortOrder}
                defaultGroup={t('contacts.groups.all-contacts')}
                PickerModalComponent={ContactPickerModal}
            />
        </>
    );
};
